// Deliverables tab (digital downloads): add form (name + file), list with
// "copy download link" and delete.
import React, { useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { CloudArrowUpIcon, CloudArrowDownIcon, LinkIcon, TrashIcon } from "@heroicons/react/24/outline"
import { UploadDeliverable, DeleteDeliverable, DeliverableLink, productEndpoint } from "../../../AdminShared/api"
import { Field, TextInput, errorMessage } from "../../../AdminShared/ui"
import { Alert, IconButton } from "../shared/shared"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

export default function DeliverablesTab({ editor }) {
  const t = useProductEditorTexts()
  const { pid, product, reload } = editor
  const [form, setForm] = useState({ name: "", file: null })
  const [error, setError] = useState("")
  const [uploading, setUploading] = useState(false)
  const [copied, setCopied] = useState("")
  const fileInput = useRef(null)
  const deliverables = product?.deliverables || []

  // what went wrong, as the server said it — "please try again" hides whether
  // the file was too large, the session expired or the product was not found
  const failed = (what, error, res) => {
    const message = errorMessage(res, "") || error?.response?.data?.error || error?.message
    setError(message ? `${what}: ${message}` : what)
  }

  const add = async () => {
    setError("")
    setUploading(true)
    try {
      const res = await UploadDeliverable(pid, form.file, form.name)
      if (errorMessage(res)) return failed(t`Upload failed`, null, res)
      setForm({ name: "", file: null })
      if (fileInput.current) fileInput.current.value = ""
      await reload()
    } catch (error) {
      failed(t`Upload failed`, error)
    } finally {
      setUploading(false)
    }
  }

  /** The signed url of a deliverable — five minutes, then it is refused. */
  const linkOf = async (d) => {
    const res = await DeliverableLink(pid, d._id)
    if (errorMessage(res)) throw new Error(errorMessage(res))
    const { token, expiresAt } = res
    return `${productEndpoint()}/deliverable?pid=${encodeURIComponent(res.pid)}&deliverableId=${encodeURIComponent(d._id)}&expiresAt=${expiresAt}&token=${token}`
  }

  const download = async (d) => {
    setError("")
    try {
      window.open(await linkOf(d), "_blank", "noopener")
    } catch (error) {
      failed(t`Download failed`, error)
    }
  }

  const copyLink = async (d) => {
    setError("")
    try {
      await navigator.clipboard?.writeText(await linkOf(d))
      setCopied(d._id)
      setTimeout(() => setCopied(""), 2000)
    } catch (error) {
      failed(t`Could not copy the link`, error)
    }
  }

  // the file goes with the entry — there is no copy of it anywhere else, and a
  // customer who bought the product downloads it from here
  const remove = (d) =>
    showPromptDialog({
      variant: "admin",
      title: t`Delete deliverable`,
      message: d.originalFileName
        ? t('Delete "%s" (%s)? The file is removed from storage and customers who bought this product can no longer download it. This cannot be undone.', d.name, d.originalFileName)
        : t('Delete "%s"? The file is removed from storage and customers who bought this product can no longer download it. This cannot be undone.', d.name),
      confirmText: t`Delete`,
      cancelText: t`Cancel`,
      onConfirm: async () => {
        setError("")
        try {
          const res = await DeleteDeliverable(pid, d._id)
          if (errorMessage(res)) return failed(t`Delete failed`, null, res)
          await reload()
        } catch (error) {
          failed(t`Delete failed`, error)
        }
      },
    })

  return (
    <div className="flex flex-col gap-3">
      <Typography component="h4" variant="admin_h4" className="my-0 inline-flex items-center gap-1.5">{t`Deliverables`}<HelpHint helpKey="vendor.product.setup#deliverables" /></Typography>
      <p className="text-sm text-admin_text_secondary mt-0 mb-2">{t`Manage downloadable files for this digital product. Customers receive access after purchase.`}</p>
      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

      <div className="p-4 border border-dashed border-admin_bordered rounded-md flex flex-col gap-3">
        <span className="text-sm font-semibold">{t`Add Deliverable`}</span>
        <Field label={t`Name`}>
          <TextInput value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder={t`e.g. E-Book PDF, Source Files`} data-testid="deliverable-name" />
        </Field>
        <div>
          <input ref={fileInput} type="file" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="deliverable-file" />
          <Button variant="admin_secondary" className="h-[32px] text-xs px-3" onClick={() => fileInput.current?.click()}>
            <CloudArrowUpIcon className="w-4 h-4" /> {form.file ? form.file.name : t`Choose File`}
          </Button>
        </div>
        {uploading && <div className="h-1 bg-admin_primary/30 overflow-hidden rounded"><div className="h-full w-1/3 bg-admin_primary animate-pulse" /></div>}
        <div className="flex justify-end">
          <Button variant="admin_primary" onClick={add} disabled={!form.file || !form.name || uploading} className="px-4">{t`Add Deliverable`}</Button>
        </div>
      </div>

      {deliverables.length > 0 && (
        <div className="flex flex-col gap-2 mt-2">
          {deliverables.map((d) => (
            <div key={d._id} className="flex items-center gap-3 p-3 border border-admin_bordered rounded-md" data-testid="deliverable-row">
              <CloudArrowDownIcon className="w-6 h-6 text-admin_primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{d.name}</div>
                <div className="text-xs text-admin_text_secondary truncate">{d.deliverableType?.toUpperCase()} · {d.originalFileName}</div>
              </div>
              <IconButton title={t`Download this file`} onClick={() => download(d)}>
                <CloudArrowDownIcon className="w-4 h-4" />
              </IconButton>
              <IconButton title={copied === d._id ? t`Link copied` : t`Copy a download link (valid 5 minutes)`} onClick={() => copyLink(d)}>
                <LinkIcon className={`w-4 h-4 ${copied === d._id ? "text-admin_success" : ""}`} />
              </IconButton>
              <IconButton title={t`Delete deliverable`} className="hover:text-admin_error" onClick={() => remove(d)}>
                <TrashIcon className="w-4 h-4" />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
